import type { Timestamp } from "firebase/firestore";

export type Brand =
  | "서울우유"
  | "매일우유"
  | "남양유업"
  | "연세우유"
  | "덴마크(동원)"
  | "파스퇴르"
  | "빙그레"
  | "기타";

export const ALL_BRANDS: Brand[] = [
  "서울우유",
  "매일우유",
  "남양유업",
  "연세우유",
  "덴마크(동원)",
  "파스퇴르",
  "빙그레",
  "기타",
];

export type StudentStatus = "active" | "paused" | "courier";

/** 조직(테넌트). 문서 ID = orgId (예: "freemilk") */
export interface Org {
  id: string;
  name: string; // 표시명 (예: "FREEMILK")
  warehouse?: { lat: number; lng: number; label?: string }; // 경로 최적화 출발점
  solapiSender?: string; // 업체별 SMS 발신번호
  bizNumber?: string; // 사업자등록번호 — 세금계산서·계좌이체 요청에 쓴다
  ceoName?: string; // 대표자명
  taxEmail?: string; // 세금계산서 받을 이메일
  ownerPhone?: string; // 사장님 연락처 — 계좌이체 안내·긴급 연락
  guardianContactPhone?: string; // 보호자 문의 번호 — 보호자 사진 화면·문자에 보인다(가입 때 필수, 2026-10-31)
  firstBilledMonth?: string; // 첫 청구 달(무료) — 서버만 씀
  cancelRequestedAt?: Timestamp; // 해지 신청 시각 — 서버만 씀. 있으면 "신청 접수됨"
  cancelRequestReason?: string;
  senderRequest?: { phone: string; requestedAt?: Timestamp; by?: string }; // 발신번호 등록 요청(서버만 씀)
  regions?: string[]; // 담당 시도 목록 — regions[0]이 주소 파싱 prefix로 쓰임 (예: "부산광역시")
  sigungu?: string[]; // 담당 시군구 이름 목록 — 주소 파싱 긴이름 우선 매칭에 사용
  createdBy?: string; // 업체를 만든 계정 uid = 대표 계정(서버만 씀). 없으면 대표 미지정 — 관리자 누구나 관리자 지정·연락처 수정
  dpaConsent?: { version: string; agreedAt?: Timestamp; uid: string; email?: string }; // 개인정보 처리위탁 계약 동의(서버만 씀, F3)
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

export interface Student {
  id: string;
  orgId?: string; // 멀티테넌시 격리 키 — 전환기(orgId 미기록 문서)엔 optional
  sourceGroup: "A" | "B" | "C" | "X";
  sheet?: string;
  no?: string;

  school: string;
  name: string;
  guardian: string;
  phone: string;

  rawAddress: string;
  refinedAddress: string;
  postalCode: string;

  gu: string;
  dong: string;
  buildingName: string;
  buildingDong: string;
  buildingHo: string;

  note?: string;
  deliveryMemo?: string; // 관리자가 지정 — 배송기사가 배송 시 반드시 확인하는 메모
  brand: Brand;
  company: string; // 물류 집행 회사 (우리) — "자사" 또는 "대행"
  isProxy?: boolean;   // 타사 대행 물류 여부
  proxyFor?: string;   // 의뢰 회사 (예: "연세우유", "남양유업")
  status: StudentStatus;
  courierFlag: boolean;
  // 택배로 돌린 이유. "month"=이번 달만(미배송 전환·기사 전환) → 새 달 시작 때 풀림. "always"=권역 밖·주소 못 찾음 → 새 달에도 택배. 없으면 옛 문서(주소로 판단)
  courierReason?: "month" | "always";
  boxesPerMonth: number;
  // 알림 받지 않음(대표 결정 Q14) — true면 배송완료 알림·완료 취소 정정 문자를 보내지 않는다(배송 기록은 그대로).
  // 보호자는 사진 화면 「알림 그만 받기」(서버 optOutGuardianNotice), 사장님은 학생 고치기 창에서 바꾼다. 기사는 못 바꾼다(firestore.rules)
  notifyOptOut?: boolean;
  notifyOptOutBy?: "guardian" | "admin";
  notifyOptOutAt?: Timestamp;

  // Geocoding cache
  lat?: number;
  lng?: number;
  geocodedAt?: Timestamp;

  // AI 주소 검토
  //   'ok'           = 자동 지오코딩 성공
  //   'ai_fixed'     = AI 가 주소를 수정 후 지오코딩 성공 (관리자 체크 대기)
  //   'needs_review' = AI 가 확신 못 함 → 주소 검토 페이지에서 수동 확인 필요
  //   'failed'       = AI 호출 자체 실패
  geocodeStatus?: "ok" | "ai_fixed" | "needs_review" | "failed";
  geocodeNeedsCheck?: boolean;        // ai_fixed 건 관리자가 체크박스로 확인 대기
  geocodeReasoning?: string;          // AI 추론 설명
  geocodeOriginalAddress?: string;    // AI 수정 전 원본 보존 (refinedAddress 를 바꿔도 원본 추적)
  geocodeCandidates?: Array<{         // needs_review 일 때 AI 제시 후보
    address: string;
    type: "ROAD" | "PARCEL";
    confidence: string;
    lat?: number;
    lng?: number;
    reasoning?: string;
  }>;
  geocodeAssumptions?: string[];

  // 라인 오버라이드(관리자가 수동 지정)
  lineOverride?: string; // 예: "1-2호 라인"

  // 최근 배송
  lastDeliveredAt?: Timestamp;
  deliveredMonths?: string[];

  // 월말 택배 운송장 (택배사 회신 엑셀에서 이름+전화로 매칭해 기록)
  trackingNo?: string;                        // (레거시) 월 구분 없는 단일 운송장 — 하위호환 표시용
  trackingByMonth?: Record<string, string>;   // 월별 운송장. 키는 "YYYY-MM"

  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

export interface DongClaim {
  id: string;
  orgId?: string;
  yearMonth: string; // YYYY-MM
  scope?: "dong" | "school"; // 없으면 "dong"(하위호환). 담당 단위.
  gu: string;
  dong: string;
  school?: string; // scope === "school" 일 때 담당 학교명
  driverUid: string;
  driverName: string;
  claimedAt: Timestamp;
  studentCount: number;
}

export interface Delivery {
  id: string;
  orgId?: string;
  studentId: string;
  studentName: string;
  school: string;
  driverUid: string;
  driverName: string;
  yearMonth: string; // YYYY-MM
  deliveredAt: Timestamp;
  photoPath: string; // storage path
  photoUrl: string;  // download URL
  proofUrl?: string; // 공개 signed URL (Cloud Function 생성)
  proofPath?: string;
  notifiedAt?: Timestamp;
  brand: Brand;
  boxCount: number;
  note?: string;
  // 사진 촬영 시점 위치 (optional)
  lat?: number;
  lng?: number;
}

export interface DriverProfile {
  uid: string;
  orgId?: string;
  displayName: string;
  phone?: string;
  role: "admin" | "driver";
  stats?: {
    total?: number;
    lastDeliveredAt?: Timestamp;
    [key: string]: any; // m_2026-05 등
  };
}

export interface AddressUpdateRecord {
  id: string;
  orgId?: string;
  emailBody: string;
  parsed: {
    studentName?: string;
    school?: string;
    guardian?: string;
    oldAddress?: string;
    newAddress?: string;
    phone?: string;
  };
  matchedStudentId?: string;
  applied: boolean;
  appliedAt?: Timestamp;
  createdAt: Timestamp;
}

// (전국화) 시군구 목록은 org 권역 설정(src/lib/region.ts) 또는 데이터의 distinct gu로 대체됨.
